import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function errDetail(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return fallback;
}

function strength(pw) {
  let n = 0;
  if (pw.length >= 8) n += 1;
  if (pw.length >= 12) n += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) n += 1;
  if (/\d/.test(pw)) n += 1;
  if (/[^A-Za-z0-9]/.test(pw)) n += 1;
  return Math.min(4, n);
}
const STRENGTH = ["Too short", "Weak", "Okay", "Good", "Strong"];

/** Change password card (Profile → Security). Signs out other devices on success. */
export function ChangePasswordCard() {
  const { user } = useAuth();
  const hasPassword = user?.auth_provider !== "google" || !!user?.has_password;
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = strength(pw);
  const mismatch = pw2.length > 0 && pw !== pw2;
  const canSubmit = pw.length >= 8 && pw === pw2 && (!hasPassword || cur.length > 0) && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: hasPassword ? cur : null, new_password: pw });
      toast.success(r.data?.message || "Password updated");
      setCur(""); setPw(""); setPw2("");
    } catch (err) {
      toast.error(errDetail(err, "Couldn't change your password"));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="change-password-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"><KeyRound className="h-5 w-5 text-primary" /></div>
        <div className="flex-1">
          <h3 className="font-serif text-lg font-semibold text-foreground">{hasPassword ? "Change password" : "Set a password"}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{hasPassword ? "Use at least 8 characters. You'll stay signed in here; every other device is signed out." : "You signed in with Google. Add a password to also sign in with email."}</p>
          <form onSubmit={submit} className="mt-4 grid gap-3 sm:max-w-md" data-testid="change-password-form">
            {hasPassword && (
              <div>
                <Label htmlFor="cur-pw">Current password</Label>
                <Input id="cur-pw" type={show ? "text" : "password"} autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" data-testid="current-password-input" />
              </div>
            )}
            <div>
              <Label htmlFor="new-pw">New password</Label>
              <div className="relative mt-1.5">
                <Input id="new-pw" type={show ? "text" : "password"} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="pr-10" data-testid="new-password-input" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" aria-label={show ? "Hide passwords" : "Show passwords"} data-testid="toggle-password-visibility">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
              {pw.length > 0 && (
                <div className="mt-2 flex items-center gap-2" data-testid="password-strength">
                  <div className="flex flex-1 gap-1">{[1, 2, 3, 4].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= s ? (s <= 1 ? "bg-destructive" : s === 2 ? "bg-amber-500" : "bg-emerald-500") : "bg-border"}`} />)}</div>
                  <span className="text-xs text-muted-foreground">{STRENGTH[s]}</span>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="new-pw2">Confirm new password</Label>
              <Input id="new-pw2" type={show ? "text" : "password"} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={`mt-1.5 ${mismatch ? "border-destructive" : ""}`} data-testid="confirm-password-input" />
              {mismatch && <p className="mt-1 text-xs text-destructive" data-testid="password-mismatch">Passwords don't match.</p>}
            </div>
            <div>
              <Button type="submit" disabled={!canSubmit} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="change-password-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : hasPassword ? "Update password" : "Set password"}</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordCard;
